import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { Toaster } from "sonner";
import HamburgerMenu from "@/components/HamburgerMenu";
import Logo from "@/components/Logo";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";
import { useAuth } from "@/context/AuthContext";

/**
 * Public landing page for account deletion. Reachable without auth so a Play
 * Store reviewer can GET it. If signed in, opens the same type-to-confirm
 * dialog as the in-app menu. If signed out, prompts to sign in first.
 */
export default function DeleteAccountPage() {
    const { user, loading } = useAuth();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    return (
        <div className="flex min-h-screen flex-col bg-white text-[#0A0A0A]">
            <Toaster position="top-center" richColors closeButton={false} />
            <header className="sticky top-0 z-10 border-b border-[#E5E5E5] bg-white/90 backdrop-blur">
                <div className="mx-auto flex w-full max-w-[820px] items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-2">
                        <HamburgerMenu />
                        <Logo size="sm" />
                    </div>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[12px] text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-[#F5F5F5]"
                        data-testid="back-to-chat"
                    >
                        <ArrowLeft size={12} strokeWidth={2.25} /> Back to chat
                    </Link>
                </div>
            </header>

            <main className="mx-auto w-full max-w-[820px] flex-1 px-5 py-16">
                <Logo size="lg" />
                <h1
                    className="mt-10 text-4xl font-semibold tracking-tight sm:text-5xl"
                    data-testid="delete-account-heading"
                >
                    Delete your Piqpath account
                </h1>
                <div className="mt-8 space-y-6 text-[15px] leading-relaxed text-[#0A0A0A]">
                    <p>
                        Deleting your account permanently removes the following data
                        from Piqpath:
                    </p>
                    <ul className="ml-5 list-disc space-y-1.5 text-[14px] text-[#262626]">
                        <li>Your account profile (email, name, sign-in details)</li>
                        <li>All your chat sessions and messages</li>
                        <li>Any share links you've created (these stop working immediately)</li>
                        <li>Your collaborator membership in other people's shared chats</li>
                        <li>Saved preferences (language, currency, region)</li>
                    </ul>
                    <p>
                        This action is <strong>irreversible</strong>. Affiliate-click
                        analytics that have already been aggregated to a regional level
                        cannot be tied back to you and remain in our reporting.
                    </p>
                </div>

                {loading ? (
                    <div className="mt-10 text-[13px] text-[#737373]" data-testid="delete-account-loading">
                        Loading your account…
                    </div>
                ) : user ? (
                    <div className="mt-10">
                        <p className="mb-3 text-[13px] text-[#525252]">
                            Signed in as{" "}
                            <span className="font-medium text-[#0A0A0A]">
                                {user.email}
                            </span>
                        </p>
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            data-testid="delete-account-button"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#262626]"
                        >
                            <Trash2 size={13} strokeWidth={2} />
                            Delete my account
                        </button>
                    </div>
                ) : (
                    <div className="mt-10 space-y-3">
                        <p className="text-[13px] text-[#525252]">
                            To delete your account, please sign in first.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate(`/login?redirect=${encodeURIComponent("/delete-account")}`)}
                            data-testid="delete-account-signin-cta"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#262626]"
                        >
                            Sign in to delete your account
                        </button>
                    </div>
                )}

                <div className="mt-16 border-t border-[#E5E5E5] pt-8">
                    <h2 className="text-[14px] font-semibold tracking-tight text-[#0A0A0A]">
                        Need help?
                    </h2>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#525252]">
                        If you can't sign in (lost password, deleted email account, etc.),
                        email{" "}
                        <a
                            href="mailto:connect@piqpath.com"
                            className="text-[#0A0A0A] underline-offset-2 hover:underline"
                            data-testid="delete-account-help-email"
                        >
                            connect@piqpath.com
                        </a>{" "}
                        from any address with proof of account ownership and we'll process
                        your deletion request manually within 30 days.
                    </p>
                </div>
            </main>

            <footer
                className="border-t border-[#E5E5E5] py-6 text-center text-[11px] text-[#A3A3A3]"
                data-testid="delete-account-footer"
            >
                <div className="space-x-3">
                    <Link to="/privacy" className="hover:text-[#0A0A0A]" data-testid="footer-link-privacy">
                        Privacy
                    </Link>
                    <Link to="/terms" className="hover:text-[#0A0A0A]" data-testid="footer-link-terms">
                        Terms
                    </Link>
                    <Link to="/contact" className="hover:text-[#0A0A0A]" data-testid="footer-link-contact">
                        Contact
                    </Link>
                    <Link to="/delete-account" className="hover:text-[#0A0A0A]" data-testid="footer-link-delete">
                        Delete account
                    </Link>
                </div>
                <div className="mt-2">Piqpath · Your trip. Your way.</div>
            </footer>

            <DeleteAccountDialog open={open} onOpenChange={setOpen} />
        </div>
    );
}
